import React, { createContext, useContext, useEffect, useState } from "react";
import { collection, onSnapshot, query, where, documentId, getDocs } from "firebase/firestore";
import { db } from "./firebaseConfig";

export interface MemberType {
    uid: string;
    name: string;
    phone: string;
    deleted: boolean;
    bankAccount: any[];
    savedKiosk: any[];
    timestamp: number;
    partnerId: string;
}

export interface PointType {
    id: string;
    docId?: string;
    inputAmt: string;
    inputType: string;
    kiosk: string;
    owner: string;
    pointAmt: number;
    pointDelta: number;
    partnerId: string;
    timestamp: number;
    type: "WITHDRAW" | "DEPOSIT";
    completed?: boolean;
    member?: MemberType | null;
}

interface PointContextType {
    points: PointType[];
    withdraws: PointType[];
}

const PointContext = createContext<PointContextType | null>(null);

export const PointProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const partnerId = sessionStorage.getItem("partnerId");
    const [points, setPoints] = useState<PointType[]>([]);
    const [withdraws, setWithdraws] = useState<PointType[]>([]);

    useEffect(() => {
        if (!partnerId) return; // partnerId가 없으면 데이터 조회하지 않음

        // receipt 컬렉션에서 partnerId로 필터링
        const receiptQuery = query(collection(db, "receipt"), where("partnerId", "==", partnerId));

        const unsubscribe = onSnapshot(receiptQuery, async (snapshot) => {
            const dataList = snapshot.docs.map((doc) => ({
                ...(doc.data() as PointType),
                id: doc.id,
            }));

            const ownerIds = Array.from(new Set(dataList.map((item) => item.owner))).filter(Boolean);

            // const membersMap: Record<string, MemberType> = {};
            // memberDocs.forEach((memberDoc) => {
            //     if (memberDoc.exists()) {
            //         membersMap[memberDoc.id] = {
            //             ...(memberDoc.data() as MemberType),
            //             uid: memberDoc.id,
            //         };
            //     }
            // });

            const membersMap: Record<string, MemberType> = {};
            if (ownerIds.length) {
                const chunkSize = 10; // Firestore 'in' 한계
                for (let i = 0; i < ownerIds.length; i += chunkSize) {
                    const chunk = ownerIds.slice(i, i + chunkSize);

                    // partnerId로 필터링된 멤버만 조회
                    const memberQuery = query(
                        collection(db, "member"),
                        where("partnerId", "==", partnerId),
                        where(documentId(), "in", chunk)
                    );

                    const snap = await getDocs(memberQuery);
                    snap.forEach((docSnap) => {
                        membersMap[docSnap.id] = {
                            ...(docSnap.data() as MemberType),
                            uid: docSnap.id,
                        };
                    });
                }
            }

            const pointsWithMember = dataList.map((point) => ({
                ...point,
                member: membersMap[point.owner] || null,
            }));

            pointsWithMember.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));

            setPoints(pointsWithMember);
            setWithdraws(pointsWithMember.filter((p) => p.type === "WITHDRAW" && p.completed !== true));
        });

        return () => unsubscribe();
    }, [partnerId]); // partnerId가 변경될 때마다 useEffect 재실행

    return <PointContext.Provider value={{ points, withdraws }}>{children}</PointContext.Provider>;
};

export const usePoint = (): PointContextType => {
    const context = useContext(PointContext);
    if (!context) {
        throw new Error("usePoint must be used within a PointProvider");
    }
    return context;
};
